import { useEffect, useState, useMemo, useCallback, memo } from "react";
import { Modal, Button, Spinner, Table } from "react-bootstrap";
import "../css/style.css";
import "../css/zerogrid.css";
import "../css/menu.css";
import "../css/modern-dashboard.css";
import api from '../api';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, ChartDataLabels, Filler);
export default function ShowMeTheNesoPage({host: hostName, changePage}) {
  const [apiKey, setApiKey] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [deviceRows, setDeviceRows] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [deviceSearchTerm, setDeviceSearchTerm] = useState("");
  const [dailySearchTerm, setDailySearchTerm] = useState("");
  const [debouncedDeviceSearch, setDebouncedDeviceSearch] = useState("");
  const [debouncedDailySearch, setDebouncedDailySearch] = useState("");
  const [coinPrice, setCoinPrice] = useState(1200); // 기본 코인시세
  const [coinPriceLoading, setCoinPriceLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [tableUpdateKey, setTableUpdateKey] = useState(0);
  const [dailyTableData, setDailyTableData] = useState({});

  const calculateCashPrice = useCallback((neso) => {
    return Number(((neso / 100000) * coinPrice).toFixed(0)).toLocaleString();
  }, [coinPrice]);

  const calculateTotalCashPrice = useCallback(() => {
    const totalNeso = deviceRows.reduce((sum, row) => {
      return sum + Number(row.neso);
    }, 0);

    return Number(((totalNeso / 100000) * coinPrice).toFixed(0)).toLocaleString();
  }, [deviceRows, coinPrice]);

  const formatToManWon = (value) => {
    const man = Math.round(value / 10000);
    return `${man}만원`;
  };

  const compareCashPrice = useCallback((neso1, neso2) => {
    const neso = neso2 - neso1;
    return Number(((neso / 100000) * coinPrice).toFixed(0)).toLocaleString();
  }, [coinPrice]);

  // Memoized cumulative income calculation
  const cumulativeIncomes = useMemo(() => {
  const groupedByDate = {};

  for (const entry of dailyRows) {
    const dateKey = new Date(entry.insertedAt).toISOString().slice(0, 10); // yyyy-mm-dd
    const serverKey = `${entry.hostname}_${entry.deviceName}`;

    if (!groupedByDate[dateKey]) groupedByDate[dateKey] = {};
    const existing = groupedByDate[dateKey][serverKey];

    if (!existing || new Date(entry.insertedAt) > new Date(existing.insertedAt)) {
      groupedByDate[dateKey][serverKey] = entry;
    }
  }

  const sortedDates = Object.keys(groupedByDate).sort();

  const cumulativeList = [];
  let totalSoFar = 0;

  for (const date of sortedDates) {
    const entries = groupedByDate[date];

    let dailyTotal = 0;

    for (const serverKey in entries) {
      const entry = entries[serverKey];
      const neso = entry.neso || 0;

      const cash = Number(
        calculateCashPrice(neso).replace(/,/g, '')
      );
      dailyTotal += cash;
    }

    totalSoFar = dailyTotal;
    cumulativeList.push({
      date: date.replace(new Date().getFullYear() + '-', ''), // 'MM-DD'
      income: totalSoFar
    });
  }

  return cumulativeList;
  }, [dailyRows, coinPrice]);

  // Memoized chart data
  const chartData = useMemo(() => {
    const labels = cumulativeIncomes.map(entry => entry.date);
    const data = cumulativeIncomes.map(entry => entry.income);

    return {
      labels,
      datasets: [{
        label: '누적 수입 (원)',
        data,
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#6366f1',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: '#4f46e5',
        datalabels: {
          align: 'top',
          anchor: 'end',
          formatter: (value) => formatToManWon(value),
          font: { 
            weight: 'bold',
            size: 11
          },
          color: '#1e293b',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          borderColor: '#6366f1',
          borderRadius: 4,
          borderWidth: 1,
          padding: 4
        }
      }]
    };
  }, [cumulativeIncomes]);

  // Memoized chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          font: {
            size: 14,
            weight: '500'
          },
          color: '#1e293b'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#6366f1',
        borderWidth: 2,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y || 0;
            return `₩${value.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
          drawBorder: false
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
          drawBorder: false
        },
        ticks: {
          callback: (value) => formatToManWon(value),
          color: '#64748b',
          font: {
            size: 12,
            weight: '500'
          }
        }
      }
    },
    elements: {
      point: {
        hoverBorderWidth: 3
      }
    }
  }), []);

  // 차트 컴포넌트
  const DailyIncomeChart = memo(({ chartData, chartOptions }) => {
    if (!chartData || chartData.labels.length === 0) {
      return (
        <div className="chart-loading">
          <i className="fas fa-chart-line fa-2x text-muted mb-2"></i>
          <p className="text-muted">로딩 중...</p>
        </div>
      );
    }

    return (
      <div style={{ height: '350px', position: 'relative' }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    );
  });

  // Memoized filtered and sorted device data
  const sortedDeviceData = useMemo(() => {
    // Filter data based on search term
    const filteredData = deviceRows.filter(row => {
      if (!debouncedDeviceSearch) return true;
      const searchLower = debouncedDeviceSearch.toLowerCase();
      return (
        row.deviceName.toLowerCase().includes(searchLower) ||
        row.neso.toString().includes(debouncedDeviceSearch)
      );
    });
  
    return [...filteredData].sort((a, b) => {
      if (!sortConfig.key) return 0;

      if(sortConfig.key === 'deviceName') {
        // 알파벳과 숫자를 분리 (예: "M2-3" -> ["M2-", 3])
        const keyA = a[sortConfig.key].replace(/[^0-9\-]/g, '');
        const keyB = b[sortConfig.key].replace(/[^0-9\-]/g, '');
        const aParts = keyA.split('-').map(Number);
        const bParts = keyB.split('-').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] ?? 0;
          const bVal = bParts[i] ?? 0;

          if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        }

        return 0; // 동일하면 그대로
      } else if (sortConfig.key === 'neso') {
        const aValue = Number(a[sortConfig.key]);
        const bValue = Number(b[sortConfig.key]);
    
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      } else if (sortConfig.key === 'price') {
        const aValue = Number(calculateCashPrice(a.neso).replace(/,/g, ''));
        const bValue = Number(calculateCashPrice(b.neso).replace(/,/g, ''));
    
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      }
  
      return 0;
    });
  }, [deviceRows, debouncedDeviceSearch, sortConfig, coinPrice]);

  // Memoized filtered device names for daily table
  const filteredDeviceNames = useMemo(() => {
    // 고유 디바이스 이름 정렬
    let deviceNames = Array.from(new Set(dailyRows.map(doc => doc.deviceName))).sort((a, b) => {
      const [aPrefix, aNum] = a.split("-");
      const [bPrefix, bNum] = b.split("-");
      const prefixCompare = aPrefix.localeCompare(bPrefix);
      return prefixCompare !== 0 ? prefixCompare : +aNum - +bNum;
    });

    // Filter device names based on search term
    if (debouncedDailySearch) {
      const searchLower = debouncedDailySearch.toLowerCase();
      deviceNames = deviceNames.filter(device => 
        device.toLowerCase().includes(searchLower)
      );
    }

    return deviceNames;
  }, [dailyRows, debouncedDailySearch]);

  // 메모이제이션된 테이블 스타일
  const tableStyles = useMemo(() => ({
    container: {
      position: 'relative',
      maxHeight: '900px',
      overflow: 'scroll',
      scrollbarWidth: 'thin', // Firefox
      scrollbarColor: '#888 #f1f1f1', // Firefox
    },
    stickyHeader: {
      position: 'sticky',
      top: 0,
      backgroundColor: '#343a40',
      color: 'white',
      zIndex: 3,
    },
    stickyFirstColumn: {
      position: 'sticky',
      left: 0,
      backgroundColor: '#fff',
      zIndex: 2,
    },
    stickyFirstColumnHeader: {
      position: 'sticky',
      left: 0,
      top: 0,
      backgroundColor: '#343a40',
      color: 'white',
      zIndex: 4,
    },
    table: {
      tableLayout: 'fixed',
      borderCollapse: 'separate',
      borderSpacing: 0,
      fontSize: '0.75rem',
    }
  }), []);

  // 메모이제이션된 formatDiff 함수
  const formatDiff = useCallback((val, unit) => {
    if (val == null) return null;
    if (val > 0) return <span className="text-success fw-bold">{`(+${val}${unit})`}</span>;
    if (val < 0) return <span className="text-danger fw-bold">{`(${val}${unit})`}</span>;
    return null;
  }, []);

  // CoinGecko API에서 실시간 코인시세 가져오기
  const fetchCoinPrice = useCallback(async () => {
    try {
      setCoinPriceLoading(true);
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=nexpace&vs_currencies=krw');
      
      if (!response.ok) {
        throw new Error('API 요청 실패');
      }
      
      const data = await response.json();
      const price = data['nexpace']?.krw;
      
      if (price) {
        setCoinPrice(price);
        console.log(`넥스페이스 가격: ₩${price.toLocaleString()}원`);
      } else {
        console.warn('코인 가격을 찾을 수 없습니다');
      }
    } catch (error) {
      console.error('코인 가격 가져오기 실패:', error.message);
      // 에러 시 기본값 유지
    } finally {
      setCoinPriceLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 코인시세 가져오기
  useEffect(() => {
    fetchCoinPrice();
    
    // 5분마다 코인시세 업데이트
    const interval = setInterval(fetchCoinPrice, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchCoinPrice]);

  // 코인 가격이 변경될 때 테이블 강제 리렌더링
  useEffect(() => {
    setTableUpdateKey(prev => prev + 1);
  }, [coinPrice]);

  // Device search debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDeviceSearch(deviceSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [deviceSearchTerm]);

  // Daily search debouncing  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDailySearch(dailySearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [dailySearchTerm]);

  // 메모이제이션된 검색 핸들러
  const handleDeviceSearchChange = useCallback((e) => {
    setDeviceSearchTerm(e.target.value);
  }, []);

  const handleDailySearchChange = useCallback((e) => {
    setDailySearchTerm(e.target.value);
  }, []);

  // dailyRows가 변경될 때 dailyTableData 초기화
  useEffect(() => {
    if (dailyRows.length > 0) {
      const newTableData = {};
      
      dailyRows.forEach(doc => {
        const day = new Date(doc.insertedAt);
        const dataKey = `${day.getFullYear()}-${1 + day.getMonth()}-${day.getDate()}`;
        
        if (!newTableData[dataKey]) {
          newTableData[dataKey] = {};
        }
        
        const neso = doc.neso || 0;
        const cashPrice = calculateCashPrice(neso);
        
        newTableData[dataKey][doc.deviceName] = {
          neso,
          cashPrice
        };
      });
      
      setDailyTableData(newTableData);
    }
  }, [dailyRows, coinPrice]);

  const processInput = () => {
    setLoading(true);

    api
      .get(`/showMeTheNesoAPI?hostName=${hostName}&apiKey=${apiKey}`)
      .then((res) => {
        setShowModal(false);
        const { total, total_yesterday, tableRows, dailyResults } = res.data;
        setSummary({ total, total_yesterday });
        setDeviceRows(tableRows);
        setDailyRows(dailyResults);
      })
      .catch((err) => {
        alert(err.response?.data || "Error occurred");
      })
      .finally(() => setLoading(false));
  };

  const formatCoinPriceTable = () => {
    return (
      <div className="coin-price-wrapper">
        <div className="coin-price-container">
          <div className="price-display-group">
            <div className="price-label">
              <i className="fas fa-dollar-sign me-1"></i>
              넥스페이스 토큰 가격 (원)
            </div>
            <div className="price-value-container">
              {coinPriceLoading ? (
                <div className="price-loading">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span className="ms-2">시세 로딩 중...</span>
                </div>
              ) : (
                <div className="price-value">
                  <span className="price-amount">₩{coinPrice.toLocaleString()}</span>
                  <button 
                    className="btn btn-sm btn-outline-primary ms-2"
                    onClick={fetchCoinPrice}
                    title="시세 새로고침"
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
              )}
            </div>
            <div className="price-info">
              <small className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                CoinGecko API에서 실시간으로 제공되는 시세입니다
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatSummaryTable = () => {
    const { total, total_yesterday } = summary;
    return (
      <div className="summary-content">
        <div className="chart-container">
          <DailyIncomeChart chartData={chartData} chartOptions={chartOptions} />
        </div>
        
        {/* 데스크톱용 테이블 */}
        <div className="summary-table-container desktop-summary">
          <table className="table table-striped modern-table">
          <thead className="thead-dark">
            <tr>
                <th><i className="fas fa-list me-1"></i>항목</th>
                <th><i className="fas fa-coins me-1"></i>네소</th>
                <th><i className="fas fa-money-bill-wave me-1"></i>현금가</th>
            </tr>
          </thead>
          <tbody>
            <tr>
                <td className="fw-bold">누적 수입</td>
                <td className="text-primary fw-bold">{total.neso.toLocaleString()}</td>
                <td className="text-success fw-bold">{calculateTotalCashPrice()}원</td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* 모바일용 카드 */}
        <div className="mobile-summary-cards">
          <div className="mobile-summary-card neso-card">
            <div className="card-icon">
              <i className="fas fa-coins"></i>
            </div>
            <div className="card-content">
              <div className="card-label">누적 네소</div>
              <div className="card-value primary">{total.neso.toLocaleString()}</div>
            </div>
          </div>
          
          <div className="mobile-summary-card cash-card">
            <div className="card-icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="card-content">
              <div className="card-label">총 현금가</div>
              <div className="card-value success">{calculateTotalCashPrice()}원</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatDeviceTable = () => {
    const handleSort = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    };
  
    // 모바일 여부 확인
    const isMobile = window.innerWidth <= 768;
    
    const tableContainerStyle = {
      maxHeight: '600px',
      overflowY: 'scroll',
      overflowX: isMobile ? 'auto' : 'hidden', // 모바일에서는 가로 스크롤 허용
      position: 'relative',
      scrollbarWidth: 'thin', // Firefox
      scrollbarColor: '#888 #f1f1f1', // Firefox
    };

    const tableWrapperStyle = {
      position: 'relative',
      borderRadius: '4px',
      border: '1px solid #dee2e6',
    };

    const stickyHeaderStyle = {
      position: 'sticky',
      top: 0,
      backgroundColor: '#343a40',
      color: 'white',
      zIndex: 2,
    };

    return (
        <div className="device-table-container">
          <div className="search-container mb-3">
            <div className="input-group modern-search">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control modern-search-input"
                placeholder="PC, 네소로 검색..."
                value={deviceSearchTerm}
                onChange={handleDeviceSearchChange}
              />
            </div>
          </div>
          <div style={tableWrapperStyle}>
            <div style={tableContainerStyle} className="custom-scrollbar">
              <Table striped bordered hover className="modern-table">
                <thead className="thead-dark modern-thead">
              <tr>
                    <th onClick={() => handleSort('deviceName')} style={{ ...stickyHeaderStyle, cursor: 'pointer' }} className="sortable-header">
                      <i className="fas fa-desktop me-1"></i>
                      PC {sortConfig.key === 'deviceName' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th onClick={() => handleSort('neso')} style={{ ...stickyHeaderStyle, cursor: 'pointer' }} className="sortable-header">
                      <i className="fas fa-coins me-1"></i>
                      네소 {sortConfig.key === 'neso' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th onClick={() => handleSort('price')} style={{ ...stickyHeaderStyle, cursor: 'pointer' }} className="sortable-header">
                      <i className="fas fa-money-bill-wave me-1"></i>
                      현금가 {sortConfig.key === 'price' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
              </tr>
            </thead>
            <tbody>
                  {sortedDeviceData.map((row, index) => (
                    <DeviceTableRow key={index} row={row} index={index} calculateCashPrice={calculateCashPrice} />
                  ))}
            </tbody>
          </Table>
            </div>
          </div>
        </div>
      );
  }

  const formatDailyTable = () => {
    if (!dailyRows.length || Object.keys(dailyTableData).length === 0) return null;
  
    // 날짜 정렬 (오름차순)
    const sortedDates = Object.keys(dailyTableData).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );
  
    return (
      <div className="daily-table-container">
        <div className="search-container mb-3">
          <div className="input-group modern-search">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control modern-search-input"
              placeholder="PC 이름으로 검색..."
              value={dailySearchTerm}
              onChange={handleDailySearchChange}
            />
          </div>
        </div>
        <div style={tableStyles.container} className="custom-scrollbar">
          <Table striped bordered hover className="text-center modern-table" style={tableStyles.table}>
            <thead className="thead-dark modern-thead">
            <tr>
                <th style={{ ...tableStyles.stickyFirstColumnHeader, width: '120px' }}>
                  <i className="fas fa-desktop me-1"></i>PC
                </th>
              {sortedDates.map((key) => {
                const [year, month, day] = key.split("-");
                  return (
                    <th 
                      key={key} 
                      style={{ ...tableStyles.stickyHeader, width: 300 }}
                    >
                      <i className="fas fa-calendar-day me-1"></i>
                      {`${month}/${day}`}
                    </th>
                  );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredDeviceNames.map((device) => (
              <DailyTableRow key={device} device={device} sortedDates={sortedDates} dailyTableData={dailyTableData} formatDiff={formatDiff} compareCashPrice={compareCashPrice} calculateCashPrice={calculateCashPrice} />
            ))}
          </tbody>
        </Table>
        </div>
      </div>
    );
  };
  
  // 네소 수정 함수
  const handleValueEdit = (deviceName, field, value) => {
    // 로컬 상태 즉시 업데이트
    setDeviceRows(prevRows => 
      prevRows.map(row => 
        row.deviceName === deviceName 
          ? { ...row, [field]: parseFloat(value) }
          : row
      )
    );

    // DB 업데이트 - object key를 사용하여 해당 디바이스의 특정 필드만 업데이트
    api.post('/neso/update', {
      host: hostName,
      device: deviceName,
      field: field,
      value: parseFloat(value)
    })
    .then(() => {
      console.log(`${field} 업데이트 성공`);
    })
    .catch((err) => {
      console.error(`${field} 업데이트 실패:`, err);
      // 실패 시 원래 값으로 되돌리기
      setDeviceRows(prevRows => 
        prevRows.map(row => 
          row.deviceName === deviceName 
            ? { ...row, [field]: row[field] }
            : row
        )
      );
    });
  };

  // 편집 모드 시작
  const startEditing = (deviceName, field, currentValue) => {
    setEditingCell(`${deviceName}-${field}`);
    setEditValue(currentValue.toString());
  };

  // 편집 모드 종료
  const finishEditing = (deviceName, field) => {
    if (editValue !== '' && !isNaN(editValue)) {
      handleValueEdit(deviceName, field, editValue);
    }
    setEditingCell(null);
    setEditValue('');
  };

  // 편집 가능한 셀 컴포넌트
  const EditableCell = memo(({ deviceName, field, value, unit }) => {
    const isEditing = editingCell === `${deviceName}-${field}`;
    
    if (isEditing) {
      return (
        <input
          type="number"
          step="0.01"
          className="form-control form-control-sm"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => finishEditing(deviceName, field)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              finishEditing(deviceName, field);
            } else if (e.key === 'Escape') {
              setEditingCell(null);
              setEditValue('');
            }
          }}
          autoFocus
          style={{ 
            width: '120px', 
            fontSize: '14px',
            textAlign: 'center',
            border: '2px solid #6366f1',
            borderRadius: '4px',
            padding: '6px 8px'
          }}
        />
      );
    }
    
    return (
      <div 
        onClick={() => startEditing(deviceName, field, value)}
        style={{ 
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        title="클릭하여 수정"
      >
        <span className="text-primary">
          {value.toLocaleString()}{unit}
        </span>
        <i className="fas fa-edit ms-1" style={{ fontSize: '10px', opacity: 0.6 }}></i>
      </div>
    );
  });

  // 날짜별 테이블용 편집 가능한 셀 컴포넌트
  const EditableDailyCell = memo(({ deviceName, dateKey, field, value, unit }) => {
    const cellKey = `${deviceName}-${dateKey}-${field}`;
    const isEditing = editingCell === cellKey;
    
    if (isEditing) {
      return (
        <input
          type="number"
          step="0.01"
          className="form-control form-control-sm"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => finishDailyEditing(deviceName, dateKey, field)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              finishDailyEditing(deviceName, dateKey, field);
            } else if (e.key === 'Escape') {
              setEditingCell(null);
              setEditValue('');
            }
          }}
          autoFocus
          style={{ 
            width: '100px', 
            fontSize: '12px',
            textAlign: 'center',
            border: '2px solid #6366f1',
            borderRadius: '4px',
            padding: '4px 6px'
          }}
        />
      );
    }
    
    return (
      <div 
        onClick={() => startDailyEditing(deviceName, dateKey, field, value)}
        style={{ 
          cursor: 'pointer',
          padding: '2px 4px',
          borderRadius: '3px',
          transition: 'background-color 0.2s',
          display: 'inline-block'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        title="클릭하여 수정"
      >
        <span className="text-primary" style={{ fontSize: '11px' }}>
          {value.toLocaleString()}{unit}
        </span>
        <i className="fas fa-edit ms-1" style={{ fontSize: '8px', opacity: 0.6 }}></i>
      </div>
    );
  });

  // 날짜별 편집 모드 시작
  const startDailyEditing = (deviceName, dateKey, field, currentValue) => {
    setEditingCell(`${deviceName}-${dateKey}-${field}`);
    setEditValue(currentValue.toString());
  };

  // 날짜별 편집 모드 종료
  const finishDailyEditing = (deviceName, dateKey, field) => {
    if (editValue !== '' && !isNaN(editValue)) {
      handleDailyValueEdit(deviceName, dateKey, field, editValue);
    }
    setEditingCell(null);
    setEditValue('');
  };

  // 날짜별 네소 수정 함수
  const handleDailyValueEdit = (deviceName, dateKey, field, value) => {
    // 서버가 기대하는 형식으로 날짜 변환 (4자리 연도 -> 2자리 연도)
    const [year, month, day] = dateKey.split("-");
    const serverDateKey = `${year.slice(-2)}-${month}-${day}`;
    
    // dailyTableData 직접 업데이트 (즉시 화면 반영)
    setDailyTableData(prevData => {
      const newData = { ...prevData };
      if (newData[dateKey] && newData[dateKey][deviceName]) {
        const updatedValue = parseFloat(value);
        newData[dateKey][deviceName] = {
          ...newData[dateKey][deviceName],
          [field]: updatedValue
        };
        
        // 현금가도 업데이트
        const neso = updatedValue;
        newData[dateKey][deviceName].cashPrice = calculateCashPrice(neso);
      }
      return newData;
    });

    // 테이블 강제 리렌더링
    setTableUpdateKey(prev => prev + 1);

    // DB 업데이트 - 해당 날짜의 특정 디바이스 데이터 업데이트
    api.post('/neso/updateDaily', {
      host: hostName,
      device: deviceName,
      date: serverDateKey, // 2자리 연도 형식으로 전송
      field: field,
      value: parseFloat(value)
    })
    .then(() => {
      console.log(`${dateKey} ${field} 업데이트 성공`);
    })
    .catch((err) => {
      console.error(`${dateKey} ${field} 업데이트 실패:`, err);
      // 실패 시 원래 값으로 되돌리기
      setDailyTableData(prevData => {
        const newData = { ...prevData };
        if (newData[dateKey] && newData[dateKey][deviceName]) {
          // 원래 값으로 복원 (dailyRows에서 다시 가져오기)
          const originalRow = dailyRows.find(row => {
            const rowDate = new Date(row.insertedAt);
            const targetDate = new Date(`20${year}`, parseInt(month) - 1, parseInt(day));
            return row.deviceName === deviceName && 
                   rowDate.getFullYear() === targetDate.getFullYear() &&
                   rowDate.getMonth() === targetDate.getMonth() &&
                   rowDate.getDate() === targetDate.getDate();
          });
          
          if (originalRow) {
            const neso = originalRow.neso || 0;
            newData[dateKey][deviceName] = {
              neso,
              cashPrice: calculateCashPrice(neso)
            };
          }
        }
        return newData;
      });
    });
  };

  // 메모이제이션된 테이블 행 컴포넌트
  const DeviceTableRow = memo(({ row, index, calculateCashPrice }) => (
    <tr key={index} className="modern-row">
      <td className="fw-bold">{row.deviceName}</td>
      <td>
        <EditableCell 
          deviceName={row.deviceName} 
          field="neso" 
          value={row.neso} 
          unit="" 
        />
      </td>
      <td className="text-success fw-bold">{calculateCashPrice(row.neso)}원</td>
    </tr>
  ));

  const DailyTableRow = memo(({ device, sortedDates, dailyTableData, formatDiff, compareCashPrice, calculateCashPrice }) => {
    let prevData = null;
    
    return (
      <tr key={device} className="modern-row">
        <td style={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 2 }} className="fw-bold">{device}</td>
        {sortedDates.map((dateKey) => {
          const data = dailyTableData[dateKey]?.[device] ?? { neso: null, cashPrice: null };
          const nesoDiff = prevData && data.neso != null ? data.neso - prevData.neso : null;
          const cashDiff = prevData ? compareCashPrice(prevData.neso, data.neso) : null;
          prevData = data.neso != null ? data : prevData;

          return (
            <td key={dateKey} style={{ backgroundColor: '#fff' }} className="daily-cell">
              {data.neso != null && (
                <div className="mb-1">
                  <small className="text-muted">네소:</small> 
                  <EditableDailyCell 
                    deviceName={device}
                    dateKey={dateKey}
                    field="neso"
                    value={data.neso}
                    unit=""
                  />
                  {formatDiff(nesoDiff, "")}
                </div>
              )}
              {data.cashPrice != null && (
                <div>
                  <small className="text-muted">현금가:</small><br/>
                  <span className="text-success fw-bold">{calculateCashPrice(data.neso)}원</span><br/>
                  {formatDiff(cashDiff ? Number(cashDiff.replace(',', '')) : 0, "원")}
                </div>
              )}
            </td>
          );
        })}
      </tr>
    );
  });

  return (
    <div className="wrap-body modern-dashboard scrollable-page">
      {loading && (
        <div className="overlay modern-overlay">
          <div className="spinner-container">
            <Spinner animation="border" variant="primary" className="modern-spinner" />
            <p className="loading-text">데이터를 불러오는 중...</p>
          </div>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false} className="modern-modal">
        <Modal.Header className="modern-modal-header">
          <Modal.Title className="modern-modal-title">
            <i className="fas fa-chart-line me-2"></i>
            네소 수입 명세서
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modern-modal-body">
          <div className="form-group modern-form-group">
            <label className="modern-label">
              <i className="fas fa-key me-2"></i>
              암호키:
            </label>
            <input
              type="password"
              className="form-control modern-input"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && processInput()}
              placeholder="암호키를 입력하세요"
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer">
          <Button variant="primary" onClick={processInput} className="modern-button">
            <i className="fas fa-sign-in-alt me-2"></i>
            확인
          </Button>
        </Modal.Footer>
      </Modal>

      <section id="container" className="modern-container">
        <div className="wrap-container">
          <div className="admin-header">
            <div className="admin-title-section">
              <h1 className="admin-title">
                <i className="fas fa-coins me-3"></i>
                실시간 네소 확인
              </h1>
              <p className="admin-subtitle">{hostName}님의 실시간 네소 수입 현황</p>
            </div>
            <button 
              className="back-to-main-btn"
              onClick={() => changePage('main')}
            >
              <i className="fas fa-home me-2"></i>
              메인으로 돌아가기
            </button>
          </div>
          
          <div className="zerogrid modern-grid">
          {!showModal && (
            <>
              <div className="modern-card price-card">
                <div className="card-header">
                  <h4 className="card-title">
                    <i className="fas fa-tags me-2"></i>
                    코인시세 설정
                  </h4>
                </div>
                <div className="card-body">
                  {formatCoinPriceTable()}
                </div>
              </div>
              
              {summary && (
                <div className="modern-card summary-card">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="fas fa-chart-area me-2"></i>
                      실시간 수입 요약
                    </h4>
                  </div>
                  <div className="card-body">
                    {formatSummaryTable()}
                  </div>
                </div>
              )}
              
              {deviceRows && (
                <div className="modern-card device-card">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="fas fa-desktop me-2"></i>
                      PC별 수입 요약
                    </h4>
                  </div>
                  <div className="card-body">
                    {formatDeviceTable()}
                  </div>
                </div>
              )}
              
              {dailyRows && (
                <div className="modern-card daily-card">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="fas fa-calendar-alt me-2"></i>
                      날짜별 수입 비교
                    </h4>
                  </div>
                  <div className="card-body" key={tableUpdateKey}>
                    {formatDailyTable()}
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </section>
    </div>
  );
}